import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { userManagementService } from '@/services/user-management.service';
import { ROUTES } from '@/routes/routes';

/**
 * D-100 — the other individuals who use this account's email address.
 *
 * The card exists to answer a question the Users list cannot: *who else is on
 * this mailbox, and are they the same person?* They are not — and the layout is
 * built to say so. Each row leads with the person's own name and **@username**,
 * because the username is what actually distinguishes them at sign-in; the
 * shared address appears once, in the header, as the thing they have in common
 * rather than as anyone's identity.
 *
 * It shows no credential material of any kind: no password state, no
 * must-change flag, no session or token data. Only who they are, what they may
 * do, and when they were last seen.
 */
export function SharedMailboxCard({
  userId,
  email,
  isSharedEmailAccount,
}: {
  userId: string;
  email: string | null;
  isSharedEmailAccount: boolean;
}) {
  const enabled = isSharedEmailAccount && !!email;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['shared-mailbox', userId],
    queryFn: () => userManagementService.sharedMailboxUsers(userId),
    enabled,
  });

  if (!enabled) return null;

  return (
    <section data-testid="shared-mailbox-card" className="rounded-lg border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-6 py-4">
        <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Shared mailbox</h2>
      </div>

      <div className="px-6 py-5">
        <p className="mb-4 text-xs text-muted-foreground">
          <span className="font-mono text-foreground">{email}</span> is used by more than one
          employee. These are <span className="font-medium">separate user accounts</span> — each
          person signs in with their own username or mobile and their own password, and their
          actions are recorded against them individually.
        </p>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {isError && (
          <p className="text-sm text-destructive">
            Could not load the other users on this mailbox.
          </p>
        )}

        {data && data.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No other active employee currently uses this address.
          </p>
        )}

        {data && data.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {data.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                <div className="min-w-[180px] flex-1">
                  <Link
                    to={ROUTES.ORG_USER_DETAIL.replace(':id', u.id)}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {u.firstName} {u.lastName}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {u.username ? `@${u.username}` : 'no username'}
                    {u.employeeCode ? ` · ${u.employeeCode}` : ''}
                  </div>
                </div>

                <div className="min-w-[140px] text-xs text-muted-foreground">
                  {u.roles.length > 0 ? u.roles.join(', ') : '—'}
                </div>

                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    u.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {u.isActive ? 'Active' : 'Inactive'}
                </span>

                <div className="min-w-[150px] text-xs text-muted-foreground">
                  {u.lastLoginAt
                    ? `Last login ${new Date(u.lastLoginAt).toLocaleString()}`
                    : 'Never signed in'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
